import { useCallback, useMemo, useState } from 'react';

interface ShareButtonsProps {
  url: string;
  title: string;
  mediaUrl?: string;
  hashtags?: string[];
  className?: string;
}

const encode = (value: string) => encodeURIComponent(value);

export default function ShareButtons({
  url,
  title,
  mediaUrl,
  hashtags = [],
  className = '',
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const supportsWebShare = useMemo(
    () => typeof (navigator as any).share === 'function',
    []
  );

  const hashtagParam = useMemo(
    () => (hashtags.length ? hashtags.join(',') : ''),
    [hashtags]
  );

  const links = useMemo(() => {
    const encodedUrl = encode(url);
    const encodedTitle = encode(title);
    const encodedMedia = mediaUrl ? encode(mediaUrl) : '';
    const encodedHashtags = hashtagParam ? encode(hashtagParam) : '';

    return {
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}${encodedHashtags ? `&hashtags=${encodedHashtags}` : ''}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}${encodedMedia ? `&media=${encodedMedia}` : ''}&description=${encodedTitle}`,
      email: `mailto:?subject=${encodedTitle}&body=Check out this article: ${encodedUrl}`,
    };
  }, [url, title, mediaUrl, hashtagParam]);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  const onWebShare = useCallback(async () => {
    if (supportsWebShare) {
      try {
        await (navigator as any).share({ title, url });
      } catch (_) {
        // user cancelled
      }
    } else {
      await onCopy();
    }
  }, [supportsWebShare, title, url, onCopy]);

  const buttonClass =
    'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-all hover:scale-105 shadow-sm';

  return (
    <div className={`${className}`}>
      <p className="text-center text-sm text-slate-500 mb-4">
        Share this article
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {/* Facebook */}
        <a
          href={links.facebook}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Facebook"
          className={`${buttonClass} bg-[#1877F2] hover:bg-[#166fe5]`}
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06C2 17.06 5.66 21.19 10.44 22v-7.04H7.9v-2.9h2.54V9.8c0-2.52 1.5-3.92 3.8-3.92 1.1 0 2.26.2 2.26.2v2.5h-1.27c-1.25 0-1.64.78-1.64 1.57v1.89h2.79l-.45 2.9h-2.34V22C18.34 21.19 22 17.06 22 12.06z" />
          </svg>
          <span className="hidden sm:inline">Facebook</span>
        </a>

        {/* X / Twitter */}
        <a
          href={links.twitter}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X"
          className={`${buttonClass} bg-black hover:bg-neutral-800`}
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.492 11.24H16.42l-5.3-6.93-6.06 6.93H1.75l7.73-8.85L1.25 2.25H7.83l4.78 6.35 5.634-6.35zm-1.16 18.45h1.833L7.01 4.075H5.047L17.084 20.7z" />
          </svg>
          <span className="hidden sm:inline">X</span>
        </a>

        {/* WhatsApp */}
        <a
          href={links.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on WhatsApp"
          className={`${buttonClass} bg-[#25D366] hover:bg-[#20bd5a]`}
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span className="hidden sm:inline">WhatsApp</span>
        </a>

        {/* LinkedIn */}
        <a
          href={links.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
          className={`${buttonClass} bg-[#0A66C2] hover:bg-[#0a5ab0]`}
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452H17.21v-5.569c0-1.329-.024-3.039-1.852-3.039-1.853 0-2.136 1.447-2.136 2.943v5.665H9v-11.5h3.112v1.571h.045c.434-.82 1.494-1.684 3.074-1.684 3.29 0 3.897 2.164 3.897 4.977v6.636zM5.337 7.433a1.808 1.808 0 11-.003-3.616 1.808 1.808 0 01.003 3.616zM6.957 20.452H3.71v-11.5h3.247v11.5z" />
          </svg>
          <span className="hidden sm:inline">LinkedIn</span>
        </a>

        {/* Pinterest */}
        <a
          href={links.pinterest}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Pinterest"
          className={`${buttonClass} bg-[#E60023] hover:bg-[#cc001f]`}
        >
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12.04 2C6.58 2 3 5.6 3 10.04c0 3.08 1.73 4.82 2.7 4.82.42 0 .67-1.17.67-1.5 0-.39-1-1.22-1-2.86 0-3.38 2.56-5.77 6.2-5.77 3 0 5.24 1.72 5.24 4.88 0 2.36-1.19 4.01-2.72 4.01-.85 0-1.49-.7-1.29-1.56.24-1.02.72-2.13.72-2.87 0-.66-.35-1.22-1.08-1.22-.86 0-1.55.89-1.55 2.09 0 .76.26 1.27.26 1.27l-1.05 4.43c-.31 1.3-.05 2.89-.03 3.05.02.15.21.3.32.12.13-.17 1.76-2.18 2.31-4.19.16-.61.93-3.53.93-3.53.46.87 1.82 1.63 3.26 1.63 4.29 0 7.2-3.1 7.2-7.27C21.07 5.24 17.51 2 12.04 2z" />
          </svg>
          <span className="hidden sm:inline">Pinterest</span>
        </a>

        {/* Email */}
        <a
          href={links.email}
          aria-label="Share via Email"
          className={`${buttonClass} bg-slate-600 hover:bg-slate-700`}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <span className="hidden sm:inline">Email</span>
        </a>

        {/* Copy Link */}
        <button
          type="button"
          onClick={onCopy}
          aria-label="Copy link"
          className={`${buttonClass} ${copied ? 'bg-green-600' : 'bg-slate-800 hover:bg-slate-700'}`}
        >
          {copied ? (
            <>
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span>Copied!</span>
            </>
          ) : (
            <>
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span>Copy Link</span>
            </>
          )}
        </button>

        {/* Native Share (mobile) */}
        {supportsWebShare && (
          <button
            type="button"
            onClick={onWebShare}
            aria-label="Share"
            className={`${buttonClass} bg-blue-600 hover:bg-blue-700`}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
            <span>Share</span>
          </button>
        )}
      </div>
    </div>
  );
}
